import { Box, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';

/**
 * Check data for timeline
 */
interface CheckData {
  status: 'UP' | 'DOWN';
  createdAt: Date | string;
}

/**
 * MiniTimeline component props
 */
interface MiniTimelineProps {
  checks: CheckData[];
  maxBars?: number;
}

/**
 * Get color based on status
 */
function getStatusColor(status: 'UP' | 'DOWN' | null): string {
  if (status === null) return '#E5E7EB';
  if (status === 'UP') return '#10B981';
  return '#EF4444';
}

/**
 * MiniTimeline component
 * Displays a compact timeline of recent checks as small colored bars
 */
export function MiniTimeline({ checks, maxBars = 24 }: MiniTimelineProps) {
  // Get the most recent checks, limited to maxBars
  const recentChecks = useMemo(() => {
    const sorted = [...checks].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return sorted.slice(0, maxBars).reverse();
  }, [checks, maxBars]);

  // Pad with empty slots if we have fewer checks than maxBars
  const bars = useMemo(() => {
    const result: (CheckData | null)[] = [];
    const emptySlots = maxBars - recentChecks.length;

    // Add empty slots first (oldest)
    for (let i = 0; i < emptySlots; i++) {
      result.push(null);
    }

    // Add actual checks (newest at end)
    result.push(...recentChecks);

    return result;
  }, [recentChecks, maxBars]);

  // Calculate uptime for tooltip
  const uptimeInfo = useMemo(() => {
    if (recentChecks.length === 0) return null;
    const upCount = recentChecks.filter((c) => c.status === 'UP').length;
    const percentage = Math.round((upCount / recentChecks.length) * 100);
    return { upCount, total: recentChecks.length, percentage };
  }, [recentChecks]);

  return (
    <Tooltip
      title={
        uptimeInfo ? (
          <Box sx={{ p: 0.25 }}>
            <Typography variant="caption" display="block">
              Last 24 hours: {uptimeInfo.percentage}% up
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {uptimeInfo.upCount}/{uptimeInfo.total} checks successful
            </Typography>
          </Box>
        ) : (
          'No check data'
        )
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          display: 'flex',
          gap: '1px',
          height: 16,
          alignItems: 'center',
          minWidth: maxBars * 4,
        }}
      >
        {bars.map((check, index) => (
          <Box
            key={index}
            sx={{
              width: 3,
              height: check ? 12 : 6,
              backgroundColor: getStatusColor(check?.status ?? null),
              borderRadius: 0.25,
              opacity: check ? 1 : 0.4,
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
}

export default MiniTimeline;
